import { type RefinementCtx, z } from "zod";

export const create = z.object({
  name: z.string().min(1, "Required"),
  testId: z.number(),
  type: z.enum(["TEXT", "NUMERIC", "HLA", "FREE_TEXT", "ORGANISM"]),
  componentType: z.enum(["RESULT", "OBSERVATION"]),
  units: z.string().nullish(),
  options: z
    .object({
      resultOptions: z.array(z.string()).optional(),
    })
    .optional(),
  instrumentCode: z.string().nullish(),
  rangeMin: z.coerce.number().nullish(),
  rangeMax: z.coerce.number().nullish(),
  rangeMinInclusive: z.boolean().prefault(false),
  rangeMaxInclusive: z.boolean().prefault(false),
  disableDoubleEntry: z.boolean().prefault(false),
  isCalculated: z.boolean().prefault(false),
  calculationMethod: z.enum(["EGFR"]).nullish(),
  calculationSourceId: z.number().nullish(),
  hlaClassification: z.string().nullish(),
  percentChangeThreshold: z.coerce.number().nullish(),
  absoluteChangeThreshold: z.coerce.number().nullish(),
  // Only consumed for TEXT specs; inert on any other type, so no schema guard.
  flagOnQualitativeChange: z.boolean().prefault(false),
  materialSpecId: z.number().nullish(),
});

export const update = z.object({ id: z.number(), patch: create.partial() });

export const ranges = (() => {
  type RangeSpec = z.infer<typeof create | typeof update>;

  const validate = (data: RangeSpec, ctx: RefinementCtx) => {
    if (data.textResultId || data.freeText) {
      return;
    }

    if (
      (data.rangeLow === undefined || data.rangeLow === null) &&
      (data.rangeHigh === undefined || data.rangeHigh === null)
    ) {
      for (const path of ["rangeLow", "rangeHigh"]) {
        ctx.addIssue({
          path: [path],
          code: "custom",
          message: "Must have at least one range value",
        });
      }
    }
    if (
      data.rangeLow !== null &&
      data.rangeLow !== undefined &&
      data.rangeHigh !== null &&
      data.rangeHigh !== undefined &&
      data.rangeLow > data.rangeHigh
    ) {
      ctx.addIssue({
        path: ["rangeLow"],
        code: "custom",
        message: "Range low cannot be higher than range high",
      });
    }
    if (
      data.ageLow !== null &&
      data.ageLow !== undefined &&
      data.ageHigh !== null &&
      data.ageHigh !== undefined &&
      data.ageLow > data.ageHigh
    ) {
      ctx.addIssue({
        path: ["ageLow"],
        code: "custom",
        message: "Age low cannot be higher than age high",
      });
    }
  };

  const create = z.object({
    testResultSpecId: z.number(),
    rangeLow: z.coerce.number().nullish(),
    rangeHigh: z.coerce.number().nullish(),
    ageLow: z.coerce.number().nullish(),
    ageHigh: z.coerce.number().nullish(),
    sex: z.enum(["M", "F"]).nullish(),
    isReference: z.boolean().prefault(false),
    textResultId: z.string().nullish(),
    freeText: z.string().nullish(),
  });

  const update = create.extend({ id: z.number() });

  return {
    create: create.superRefine(validate),
    update: update.superRefine(validate),
  };
})();

export const attributes = (() => {
  const create = z.object({
    testResultSpecId: z.number(),
    name: z.string().min(1, "Required"),
    type: z.enum(["TEXT", "NUMERIC", "BOOLEAN"]),
    code: z.string().min(1, "Required"),
    description: z.string().nullish(),
    isRequired: z.boolean().prefault(false),
  });

  const update = create.extend({ id: z.number() });

  return {
    create,
    update,
  };
})();
